<template>
  <div class="schema-field-settings pa-4">
    <v-row dense>
      <!-- Basic -->
      <v-col v-if="field.type !== 'fragment'" cols="3">
        <v-text-field
          :value="field.key"
          label="Key"
          dense
          outlined
          :rules="keyRules"
          :error-messages="keyError"
          @input="update('key', $event)"
        />
      </v-col>
      <v-col v-if="field.type !== 'fragment'" cols="3">
        <v-text-field
          :value="field.label"
          label="Label"
          dense
          outlined
          @input="update('label', $event)"
        />
      </v-col>
      <v-col cols="3">
        <v-select
          :value="field.type"
          :items="fieldTypes"
          label="Type"
          dense
          outlined
          @input="onTypeChange"
        />
      </v-col>
      <v-col v-if="field.type === 'fragment'" cols="6">
        <v-autocomplete
          :value="field.$ref"
          :items="fragmentTypes"
          item-text="name"
          item-value="slug"
          label="Fragment"
          dense
          outlined
          hide-details
          @input="update('$ref', $event)"
        />
      </v-col>
      <v-col v-if="field.type !== 'fragment' && isTopLevel && fieldsetOptions.length" cols="3">
        <v-select
          :value="field.fieldset"
          :items="fieldsetOptions"
          label="Fieldset"
          dense
          outlined
          clearable
          @input="update('fieldset', $event)"
        />
      </v-col>
      <v-col v-if="field.type !== 'fragment'" cols="2">
        <v-select
          :value="field.cols"
          :items="colsOptions"
          label="Width"
          dense
          outlined
          clearable
          hide-details
          @input="update('cols', $event ? Number($event) : null)"
        />
      </v-col>
    </v-row>

    <!-- Options -->
    <v-row v-if="showOptions" dense>
      <v-col v-if="showIndexed" cols="2">
        <v-switch
          :input-value="field.indexed"
          label="Indexed"
          dense
          hide-details
          class="mt-0"
          @change="update('indexed', !!$event)"
        />
      </v-col>
      <v-col v-if="showTranslatable" cols="2">
        <v-switch
          :input-value="field.translatable"
          label="Translatable"
          dense
          hide-details
          class="mt-0"
          @change="update('translatable', !!$event)"
        />
      </v-col>
      <v-col v-if="showExpanded" cols="2">
        <v-switch
          :input-value="field.expanded"
          label="Expanded"
          dense
          hide-details
          class="mt-0"
          @change="update('expanded', !!$event)"
        />
      </v-col>
    </v-row>


    <!-- Depends On -->
    <div class="mt-3">
      <div class="d-flex align-center mb-2">
        <span class="text-caption">Conditional visibility (depends_on)</span>
        <v-spacer />
        <v-btn x-small text color="primary" @click="addDependsOnGroup">
          + Add OR group
        </v-btn>
      </div>
      <div v-if="dependsOnGroups.length">
        <div v-for="(group, gIdx) in dependsOnGroups" :key="'g-' + gIdx" class="depends-on-group mb-2">
          <div class="d-flex align-center mb-1">
            <span class="text-caption grey--text">{{ gIdx > 0 ? 'OR' : '' }} Group {{ gIdx + 1 }} (AND)</span>
            <v-spacer />
            <v-btn x-small text color="primary" @click="addConditionToGroup(gIdx)">+ Condition</v-btn>
            <v-btn x-small icon color="error" @click="removeDependsOnGroup(gIdx)">
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-row v-for="(cond, cIdx) in group" :key="'c-' + cIdx" dense class="mb-1">
            <v-col cols="4">
              <v-select
                :value="cond.field"
                :items="siblingFieldOptions"
                label="Field"
                dense
                outlined
                hide-details
                @input="updateCondition(gIdx, cIdx, 'field', $event)"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                :value="cond.operator"
                :items="operatorOptions"
                label="Operator"
                dense
                outlined
                hide-details
                @input="updateCondition(gIdx, cIdx, 'operator', $event)"
              />
            </v-col>
            <v-col cols="4">
              <v-combobox
                :value="cond.value"
                :items="getConditionHints(cond.field)"
                label="Value"
                dense
                outlined
                hide-details
                @input="updateCondition(gIdx, cIdx, 'value', $event)"
              />
            </v-col>
            <v-col cols="1" class="d-flex align-center">
              <v-btn icon x-small color="error" @click="removeCondition(gIdx, cIdx)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <!-- Display widget (array:* only) -->
    <div v-if="showWidget" class="mt-3">
      <div class="text-caption mb-1">Display</div>
      <v-radio-group
        :value="field.widget || 'chips'"
        row
        dense
        hide-details
        class="mt-0 mb-2"
        @change="update('widget', $event)"
      >
        <v-radio v-for="opt in widgetOptions" :key="opt.value" :label="opt.text" :value="opt.value" />
      </v-radio-group>
    </div>

    <!-- Values Source (unified enum + data source) -->
    <div v-if="showValuesSource" class="mt-3">
      <div class="text-caption mb-1">Values</div>
      <v-radio-group
        :value="valuesMode"
        row
        dense
        hide-details
        class="mt-0 mb-2"
        @change="onValuesModeChange"
      >
        <v-radio label="None" :value="'none'" />
        <v-radio label="Manual list" :value="'enum'" />
        <v-radio label="Preset" :value="'preset'" />
        <v-radio label="CMS Items" :value="'item'" />
        <v-radio label="External API" :value="'api'" />
      </v-radio-group>

      <!-- Manual list (enum) -->
      <div v-if="valuesMode === 'enum'">
        <div class="enum-list-scroll">
          <v-row v-for="(item, idx) in enumItems" :key="idx" dense class="mb-1">
            <v-col cols="5">
              <v-text-field
                :value="item.value"
                label="Value"
                dense
                outlined
                hide-details
                @input="updateEnumItem(idx, 'value', $event)"
              />
            </v-col>
            <v-col cols="5">
              <v-text-field
                :value="item.label"
                label="Label"
                dense
                outlined
                hide-details
                @input="updateEnumItem(idx, 'label', $event)"
              />
            </v-col>
            <v-col cols="2" class="d-flex align-center">
              <v-btn icon x-small color="error" @click="removeEnumItem(idx)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <div class="d-flex mt-1">
          <v-btn x-small text color="primary" @click="addEnumItem">
            + Add Value
          </v-btn>
          <v-spacer />
          <v-btn x-small text @click="$refs.csvInput.click()">
            <v-icon x-small left>mdi-file-delimited</v-icon> Import CSV
          </v-btn>
          <input ref="csvInput" type="file" accept=".csv" style="display: none;" @change="onImportCsv" />
        </div>
      </div>

      <!-- Preset -->
      <v-row v-if="valuesMode === 'preset'" dense>
        <v-col cols="4">
          <v-select
            :value="source.preset"
            :items="presetOptions"
            label="Preset list"
            dense
            outlined
            hide-details
            @input="updateSource('preset', $event)"
          />
        </v-col>
      </v-row>

      <!-- CMS Items source -->
      <v-row v-if="valuesMode === 'item'" dense>
        <v-col cols="4">
          <v-autocomplete
            :value="sourceFilter('type_slug')"
            :items="cmsTypes"
            item-text="name"
            item-value="slug"
            label="Type"
            dense
            outlined
            hide-details
            clearable
            @input="updateSourceFilter('type_slug', $event)"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            :value="source.value_field || 'item_id'"
            label="Value field"
            dense
            outlined
            hide-details
            @input="updateSource('value_field', $event)"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            :value="source.label_field || 'name'"
            label="Label field"
            dense
            outlined
            hide-details
            @input="updateSource('label_field', $event)"
          />
        </v-col>
      </v-row>

      <!-- API source -->
      <v-row v-if="valuesMode === 'api'" dense>
        <v-col cols="3">
          <v-text-field
            :value="source.service"
            label="Service"
            dense
            outlined
            hide-details
            @input="updateSource('service', $event)"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            :value="source.method"
            label="Method"
            dense
            outlined
            hide-details
            @input="updateSource('method', $event)"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="source.value_field"
            label="Value field"
            dense
            outlined
            hide-details
            @input="updateSource('value_field', $event)"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="source.label_field"
            label="Label field"
            dense
            outlined
            hide-details
            @input="updateSource('label_field', $event)"
          />
        </v-col>
        <v-col cols="2">
          <v-select
            :value="source.depends_on_field"
            :items="siblingFieldOptions"
            label="Depends on"
            dense
            outlined
            hide-details
            clearable
            @input="updateSource('depends_on_field', $event)"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Nested Schema -->
    <div v-if="showNested" class="mt-3">
      <div class="text-caption mb-1">Nested Schema</div>
      <v-radio-group
        :value="nestedMode"
        row
        dense
        hide-details
        class="mt-0 mb-2"
        @change="onNestedModeChange"
      >
        <v-radio label="Define inline" value="inline" />
        <v-radio label="Use existing ($ref)" value="ref" />
      </v-radio-group>

      <div v-if="nestedMode === 'inline'">
        <v-btn small text color="primary" @click="$emit('edit-nested')">
          <v-icon small left>mdi-arrow-right</v-icon>
          Edit nested fields
          <span v-if="nestedCount" class="ml-1 grey--text">({{ nestedCount }})</span>
        </v-btn>
      </div>
      <div v-else>
        <v-autocomplete
          :value="field.$ref"
          :items="fragmentTypes"
          item-text="name"
          item-value="slug"
          label="Reference Type (fragment)"
          dense
          outlined
          hide-details
          @input="onNestedRefChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import {
  FIELD_TYPES,
  INDEXABLE_TYPES,
  TRANSLATABLE_TYPES,
  SOURCE_TYPES_ALLOWED,
  NESTED_TYPES,
  ENUM_TYPES,
  WIDGET_OPTIONS,
  WIDGET_TYPES,
} from './schemaConstants'

export default {
  props: {
    field: {
      type: Object,
      required: true,
    },
    siblingFields: {
      type: Array,
      default: () => [],
    },
    fieldsetOptions: {
      type: Array,
      default: () => [],
    },
    cmsTypes: {
      type: Array,
      default: () => [],
    },
    fragmentTypes: {
      type: Array,
      default: () => [],
    },
    isTopLevel: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fieldTypes: FIELD_TYPES,
      widgetOptions: WIDGET_OPTIONS,
    }
  },
  computed: {
    keyRules() {
      return [
        v => !!v || 'Key is required',
        v => /^[a-z][a-z0-9_]*$/.test(v) || 'Lowercase letters, numbers, underscores. Must start with letter',
      ]
    },
    keyError() {
      const dup = this.siblingFields.some(f => f !== this.field && f.key === this.field.key && this.field.key)
      if (dup) {
        return 'Duplicate key'
      }
      return ''
    },
    showOptions() {
      return this.showIndexed || this.showTranslatable || this.showExpanded
    },
    showIndexed() {
      return INDEXABLE_TYPES.includes(this.field.type)
    },
    showTranslatable() {
      return TRANSLATABLE_TYPES.includes(this.field.type)
    },
    showExpanded() {
      return this.field.type === 'object'
    },
    showValuesSource() {
      return ENUM_TYPES.includes(this.field.type) || SOURCE_TYPES_ALLOWED.includes(this.field.type)
    },
    showWidget() {
      return WIDGET_TYPES.includes(this.field.type)
    },
    valuesMode() {
      if (this.field.enum && Array.isArray(this.field.enum)) {
        return 'enum'
      }
      if (this.field.source && this.field.source.type) {
        return this.field.source.type
      }
      return 'none'
    },
    enumItems() {
      const raw = this.field.enum || []
      return raw.map(item => {
        if (typeof item === 'string') {
          return { value: item, label: item }
        }
        return { value: item.value || '', label: item.label || '' }
      })
    },
    presetOptions() {
      return [
        { text: 'Currencies', value: 'currencies' },
      ]
    },
    showNested() {
      return NESTED_TYPES.includes(this.field.type)
    },
    source() {
      return this.field.source || {}
    },
    sourceType() {
      return this.source.type || null
    },
    nestedMode() {
      return this.field.$ref !== undefined ? 'ref' : 'inline'
    },
    nestedCount() {
      if (this.field.schema && Array.isArray(this.field.schema)) {
        return this.field.schema.length
      }
      return 0
    },
    colsOptions() {
      return [
        { text: 'Auto', value: null },
        { text: '1/12', value: 1 },
        { text: '2/12', value: 2 },
        { text: '3/12 (1/4)', value: 3 },
        { text: '4/12 (1/3)', value: 4 },
        { text: '6/12 (1/2)', value: 6 },
        { text: '8/12 (2/3)', value: 8 },
        { text: '9/12 (3/4)', value: 9 },
        { text: '12/12 (full)', value: 12 },
      ]
    },
    dependsOnGroups() {
      if (!this.field.depends_on || !Array.isArray(this.field.depends_on)) {
        return []
      }
      return this.field.depends_on
    },
    operatorOptions() {
      return [
        { text: '==', value: '==' },
        { text: '!=', value: '!=' },
        { text: '>', value: '>' },
        { text: '>=', value: '>=' },
        { text: '<', value: '<' },
        { text: '<=', value: '<=' },
        { text: 'is empty', value: '==empty' },
        { text: 'not empty', value: '!=empty' },
      ]
    },
    siblingFieldOptions() {
      return this.siblingFields
        .filter(f => f.key && f.key !== this.field.key)
        .map(f => ({ text: f.label || f.key, value: f.key }))
    },
  },
  methods: {
    update(prop, value) {
      const updated = cloneDeep(this.field)
      if (value === null || value === undefined || value === '') {
        delete updated[prop]
      } else {
        updated[prop] = value
      }
      this.$emit('update', updated)
    },
    updateEnumItem(idx, prop, value) {
      const items = cloneDeep(this.enumItems)
      items[idx][prop] = value
      this.update('enum', items)
    },
    addEnumItem() {
      const items = cloneDeep(this.enumItems)
      items.push({ value: '', label: '' })
      this.update('enum', items)
    },
    removeEnumItem(idx) {
      const items = cloneDeep(this.enumItems)
      items.splice(idx, 1)
      if (items.length === 0) {
        this.update('enum', null)
      } else {
        this.update('enum', items)
      }
    },
    onImportCsv(e) {
      const file = e.target.files[0]
      if (!file) {
        return
      }
      const reader = new FileReader()
      reader.onload = (event) => {
        const text = event.target.result
        const lines = text.split('\n').map(l => l.trim()).filter(l => l)
        const items = lines.map(line => {
          const parts = line.split(/[,;]/).map(p => p.trim().replace(/^["']|["']$/g, ''))
          return {
            value: parts[0] || '',
            label: parts[1] || parts[0] || '',
          }
        })
        if (items.length) {
          this.update('enum', items)
        }
      }
      reader.readAsText(file)
      e.target.value = ''
    },
    onTypeChange(newType) {
      const updated = cloneDeep(this.field)
      updated.type = newType

      // Clean inapplicable properties
      if (!INDEXABLE_TYPES.includes(newType)) {
        delete updated.indexed
      }
      if (!TRANSLATABLE_TYPES.includes(newType)) {
        delete updated.translatable
      }
      if (!ENUM_TYPES.includes(newType) && !SOURCE_TYPES_ALLOWED.includes(newType)) {
        delete updated.enum
        delete updated.source
      }
      if (!NESTED_TYPES.includes(newType) && newType !== 'fragment') {
        delete updated.schema
        delete updated.$ref
        delete updated.expanded
      }
      if (newType === 'fragment') {
        delete updated.indexed
        delete updated.translatable
        delete updated.enum
        delete updated.source
        delete updated.schema
        delete updated.expanded
      }

      this.$emit('update', updated)
    },
    getConditionHints(fieldKey) {
      if (!fieldKey) {
        return []
      }
      const dep = this.siblingFields.find(f => f.key === fieldKey)
      if (dep && dep.enum) {
        return dep.enum.map(item => {
          if (typeof item === 'string') {
            return item
          }
          return item.value || ''
        })
      }
      return []
    },
    addDependsOnGroup() {
      const updated = cloneDeep(this.field)
      if (!updated.depends_on || !Array.isArray(updated.depends_on)) {
        updated.depends_on = []
      }
      updated.depends_on.push([{ field: '', operator: '==', value: '' }])
      this.$emit('update', updated)
    },
    removeDependsOnGroup(gIdx) {
      const updated = cloneDeep(this.field)
      updated.depends_on.splice(gIdx, 1)
      if (!updated.depends_on.length) {
        delete updated.depends_on
      }
      this.$emit('update', updated)
    },
    addConditionToGroup(gIdx) {
      const updated = cloneDeep(this.field)
      updated.depends_on[gIdx].push({ field: '', operator: '==', value: '' })
      this.$emit('update', updated)
    },
    removeCondition(gIdx, cIdx) {
      const updated = cloneDeep(this.field)
      updated.depends_on[gIdx].splice(cIdx, 1)
      if (!updated.depends_on[gIdx].length) {
        updated.depends_on.splice(gIdx, 1)
      }
      if (!updated.depends_on.length) {
        delete updated.depends_on
      }
      this.$emit('update', updated)
    },
    updateCondition(gIdx, cIdx, prop, value) {
      const updated = cloneDeep(this.field)
      updated.depends_on[gIdx][cIdx][prop] = value
      this.$emit('update', updated)
    },
    onValuesModeChange(mode) {
      const updated = cloneDeep(this.field)
      // Clear both enum and source first
      delete updated.enum
      delete updated.source

      if (mode === 'enum') {
        updated.enum = []
      } else if (mode === 'preset') {
        updated.source = { type: 'preset', preset: 'currencies' }
      } else if (mode === 'item') {
        updated.source = { type: 'item', value_field: 'item_id', label_field: 'name', filter: {} }
      } else if (mode === 'api') {
        updated.source = { type: 'api' }
      }
      // 'none' — both deleted already

      this.$emit('update', updated)
    },
    sourceFilter(key) {
      if (!this.source.filter) {
        return null
      }
      return this.source.filter[key]
    },
    updateSourceFilter(key, value) {
      const updated = cloneDeep(this.field)
      if (!updated.source) {
        updated.source = { type: 'item', filter: {} }
      }
      if (!updated.source.filter) {
        updated.source.filter = {}
      }
      if (value) {
        updated.source.filter[key] = value
      } else {
        delete updated.source.filter[key]
      }
      this.$emit('update', updated)
    },
    updateSource(key, value) {
      const updated = cloneDeep(this.field)
      if (!updated.source) {
        updated.source = { type: this.sourceType }
      }
      updated.source[key] = value
      this.$emit('update', updated)
    },
    updateStaticItem(idx, prop, value) {
      const updated = cloneDeep(this.field)
      if (!updated.source || !updated.source.items) {
        return
      }
      updated.source.items[idx][prop] = value
      this.$emit('update', updated)
    },
    addStaticItem() {
      const updated = cloneDeep(this.field)
      if (!updated.source) {
        updated.source = { type: 'static', items: [] }
      }
      if (!updated.source.items) {
        updated.source.items = []
      }
      updated.source.items.push({ value: '', label: '' })
      this.$emit('update', updated)
    },
    removeStaticItem(idx) {
      const updated = cloneDeep(this.field)
      updated.source.items.splice(idx, 1)
      this.$emit('update', updated)
    },
    onNestedModeChange(mode) {
      const updated = cloneDeep(this.field)
      if (mode === 'ref') {
        if (updated.$ref === undefined) {
          updated.$ref = ''
        }
        // schema НЕ трогаем — пока фрагмент не выбран, inline-схема остаётся как есть
      } else {
        delete updated.$ref
        if (!Array.isArray(updated.schema)) {
          updated.schema = []
        }
      }
      this.$emit('update', updated)
    },
    onNestedRefChange(slug) {
      const updated = cloneDeep(this.field)
      if (slug) {
        updated.$ref = slug
        delete updated.schema   // выбран реальный фрагмент → inline-схема больше не нужна
      } else {
        updated.$ref = ''       // очистили выбор — остаёмся в ref-режиме с пустым ref, schema не трогаем
      }
      this.$emit('update', updated)
    },
  },
}
</script>

<style scoped>
.depends-on-group {
  border: 1px solid #eee !important;
  border-radius: 4px !important;
  padding: 8px !important;
}
.enum-list-scroll {
  max-height: 640px !important;
  overflow-y: auto !important;
}
</style>
